import { useState, useRef, useMemo } from "react";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import {
  InputGroup,
  FieldWrappers,
  Label,
  Wrapper,
  CombineInputGroup,
  SubmitBtn,
} from "../../Styles/InputStyles";
import { toast } from "react-toastify";
import { MdClose, MdEdit } from "react-icons/md";
import tw from "tailwind-styled-components";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import "react-dropdown/style.css";
import { FormControl } from "@mui/material";
import { useSelector } from "react-redux";
import SelectLocationModal from "../../SelectLocationModal";
import { Autocomplete } from "@react-google-maps/api";
import SelectOptionModal from "../../SelectOptionModal";
import Config, {
  landlord_property_type,
  listing_type,
  bhk_options,
} from "../../../Config";
import { preferredTenantOptions } from "../../../helpers";

const PropertyBasicInfo = ({
  editPropBasicInfoLoading,
  editPropBasicInfoMutate,
  apiData,
}) => {
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isBhkModalOpen, setIsBhkModalOpen] = useState(false);
  const locations = useSelector((state) => state.LocationsReducer.locations);
  const filteredLocations = useMemo(() => {
    return locations?.filter((location) => location.id !== 0) || [];
  }, [locations]);

  const formikRef = useRef(null);

  const autocompleteRef = useRef(null);

  const handlePlaceSelect = (setFieldValue) => {
    const place = autocompleteRef.current.getPlace();

    if (place && place.formatted_address) {
      setFieldValue("address", place.formatted_address);
    }
  };
  const propertyComesThrough = [
    { value: 1, label: "Self" },
    { value: 2, label: "News Paper" },
    { value: 3, label: "Broker" },
    { value: 4, label: "Portal" },
  ];

  const validationSchema = Yup.object({
    address: Yup.string()
      .required("Address is required")
      .min(5, "Address must be at least 5 characters"),
    pincode: Yup.string()
      .test(
        "pincode-format",
        "Pincode must be 6 digits",
        (value) => !value || /^\d{6}$/.test(value),
      )
      .nullable(),
  });

  const initialValues = {
    inventoryName: apiData?.inventoryName || "",
    perfectFit: apiData?.tenantPreference || "",
    securityDeposit: apiData?.security || "",
    building: apiData?.building || "",
    agreementPeriod: apiData?.agreementPeriod || "",
    noticePeriod: apiData?.noticePeriod || "",
    lockInPeriod: apiData?.lockInPeriod || "",
    id: apiData?.id || "",
    flatNo: apiData?.flatNo || "",
    locationId: apiData?.locationId || "",
    address: apiData?.address || "",
    pincode: apiData?.pincode || "",
    inventoryType: apiData?.inventoryType || "",
    inventorySource: apiData?.inventorySource || "",
    rentalCycle: apiData?.rentalCycle || "",
    inventorySubType: apiData?.inventorySubType || "",
    listingType: Number(apiData?.listingType) || "",
    tenantPreference: apiData?.tenantPreference || "",
  };

  const handleSubmit = (values) => {
    if (!values.address || values.address.trim().length < 5) {
      toast.error("Address must be at least 5 characters long");
      return;
    }

    editPropBasicInfoMutate({
      inventoryId: apiData.id,
      listingType: values.listingType,
      inventoryType: values.inventoryType,
      buildingName: values.building,
      inventoryName: values?.inventoryName,
      inventorySubType: values.inventorySubType,
      flatNo: values.flatNo,
      locationId: values.locationId,
      address: values.address,
      source: values.inventorySource || "",
      agreementPeriod: values.agreementPeriod,
      noticePeriod: values.noticePeriod,
      lockInPeriod: values.lockInPeriod,
      tenantPreference: values.tenantPreference || "",
    });
  };

  const getInputClass = (isDisabled) => {
    return isDisabled
      ? "bg-gray-100 cursor-not-allowed"
      : "pl-2 relative rounded-md w-full flex items-center border border-gray-400 h-12 pr-6";
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize={true}
    >
      {({
        values,
        setFieldValue,
        handleChange,
        handleBlur,
        errors,
        touched,
      }) => {
        const getLocationLabel = () => {
          const selectedRole = filteredLocations.find(
            (role) => role.id === values?.locationId,
          );
          return selectedRole ? selectedRole.name : "Select Location";
        };

        const handleLocationSelect = (selectedValue) => {
          setFieldValue("locationId", selectedValue);
          setFieldValue("locationTouched", true);
        };

        const handleBhkSelect = (selectedValue) => {
          setFieldValue("inventorySubType", selectedValue);
          setFieldValue("bhkTouched", true);
        };
        const isBhkEditable = [
          Config.LANDLORD_PROPERTY_TYPE.APARTMENT_FLAT,
          Config.LANDLORD_PROPERTY_TYPE.VILLA_INDEPENDENT_FLOOR,
        ].includes(Number(values.inventoryType));

        return (
          <Form>
            <Wrapper>
              <CombineInputGroup>
                <InputGroup>
                  <Label>Listing Type *</Label>
                  <FormControl fullWidth>
                    <Select
                      className="px-2"
                      id="listingType"
                      name="listingType"
                      value={values.listingType}
                      onChange={(e) =>
                        setFieldValue("listingType", e.target.value)
                      }
                      onBlur={handleBlur}
                      style={{ height: "46px" }}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <span className="text-gray-400">
                              Select listing type
                            </span>
                          );
                        }
                        const option = listing_type.find(
                          (opt) => opt.value === selected,
                        );
                        return option ? option.label : selected;
                      }}
                    >
                      <MenuItem value="" disabled>
                        <span className="text-gray-400">Listing Type</span>
                      </MenuItem>
                      {listing_type.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          disabled={option.label === "Sale"}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="inventoryName">Inventory Name</Label>
                  <FieldWrapper>
                    <Field
                      type="text"
                      name="inventoryName"
                      id="inventoryName"
                      autoComplete="off"
                      // required
                      className={getInputClass(false)}
                    />
                  </FieldWrapper>
                </InputGroup>
              </CombineInputGroup>

              <CombineInputGroup>
                <InputGroup>
                  <Label htmlFor="inventoryType">Inventory Type *</Label>
                  <FormControl className="">
                    <Select
                      className="px-2"
                      id="inventoryType"
                      name="inventoryType"
                      value={values.inventoryType}
                      // onChange={(e) =>
                      //     setFieldValue("propertyType", e.target.value)
                      // }
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setFieldValue("inventoryType", value);

                        if (
                          ![
                            Config.LANDLORD_PROPERTY_TYPE.APARTMENT_FLAT,
                            Config.LANDLORD_PROPERTY_TYPE
                              .VILLA_INDEPENDENT_FLOOR,
                          ].includes(value)
                        ) {
                          setFieldValue("inventorySubType", "");
                        }
                      }}
                      onBlur={handleBlur}
                      style={{ height: "46px" }}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <span className="text-gray-400">
                              Select inventory type
                            </span>
                          );
                        }
                        const option = landlord_property_type.find(
                          (opt) => opt.value === selected,
                        );
                        return option ? option.label : selected;
                      }}
                    >
                      <MenuItem value="" disabled>
                        <span className="text-gray-400">Inventory Type</span>
                      </MenuItem>
                      {landlord_property_type.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="bhk">Bhk *</Label>

                  <FieldWrappers
                    onClick={() => {
                      if (isBhkEditable) {
                        setIsBhkModalOpen(true);
                      }
                    }}
                    className={`border border-gray-400 ${
                      !isBhkEditable
                        ? "bg-gray-100 cursor-not-allowed opacity-70"
                        : "cursor-pointer"
                    }`}
                  >
                    <Field
                      type="text"
                      name="bhk"
                      id="bhk"
                      autoComplete="off"
                      // readOnly
                      // value={getLocationLabel()}
                      readOnly
                      value={
                        bhk_options.find(
                          (opt) => opt.value === values.inventorySubType,
                        )?.label || "Select BHK"
                      }
                      className="w-full border-none outline-none bg-transparent cursor-pointer"
                    />
                    <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                  </FieldWrappers>

                  {touched.inventorySubType && errors.inventorySubType && (
                    <p className="text-rose-600 text-sm mt-1">
                      {errors.inventorySubType}
                    </p>
                  )}
                </InputGroup>

                <InputGroup>
                  <Label>Inventory Source</Label>
                  <FormControl fullWidth>
                    <Select
                      className="px-2"
                      id="inventorySource"
                      name="inventorySource"
                      value={values.inventorySource}
                      onChange={(e) =>
                        setFieldValue("inventorySource", e.target.value)
                      }
                      onBlur={handleBlur}
                      style={{ height: "46px" }}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <span className="text-gray-400">
                              Select inventory source
                            </span>
                          );
                        }
                        const option = propertyComesThrough.find(
                          (opt) => opt.value === selected,
                        );
                        return option ? option.label : selected;
                      }}
                    >
                      <MenuItem value="" disabled>
                        <span className="text-gray-400">
                          Select inventory source
                        </span>
                      </MenuItem>
                      {propertyComesThrough.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </InputGroup>
              </CombineInputGroup>

              <CombineInputGroup>
                <InputGroup>
                  <Label htmlFor="agreementPeriod">Agreement Period *</Label>
                  <FormControl fullWidth>
                    <Select
                      className="px-2"
                      id="agreementPeriod"
                      name="agreementPeriod"
                      value={values.agreementPeriod}
                      onChange={(e) =>
                        setFieldValue("agreementPeriod", e.target.value)
                      }
                      // required
                      style={{ height: "46px" }}
                    >
                      {Array.from({ length: 36 }, (_, i) => i + 1).map(
                        (month) => (
                          <MenuItem key={month} value={month}>
                            {month} {month === 1 ? "Month" : "Months"}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  </FormControl>
                </InputGroup>
                <InputGroup>
                  <Label htmlFor="noticePeriod">Notice Period *</Label>
                  <FormControl fullWidth>
                    <Select
                      className="px-2"
                      id="noticePeriod"
                      name="noticePeriod"
                      value={values.noticePeriod}
                      onChange={(e) =>
                        setFieldValue("noticePeriod", e.target.value)
                      }
                      // required
                      style={{ height: "46px" }}
                    >
                      {Array.from({ length: 6 }, (_, i) => i + 1).map(
                        (month) => (
                          <MenuItem key={month} value={month}>
                            {month} {month === 1 ? "Month" : "Months"}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  </FormControl>
                </InputGroup>
                <InputGroup>
                  <Label htmlFor="lockInPeriod">Lock-In Period *</Label>
                  <FormControl fullWidth>
                    <Select
                      className="px-2"
                      id="lockInPeriod"
                      name="lockInPeriod"
                      value={values.lockInPeriod}
                      onChange={(e) =>
                        setFieldValue("lockInPeriod", e.target.value)
                      }
                      // required
                      style={{ height: "46px" }}
                    >
                      {Array.from({ length: 11 }, (_, i) => i + 1).map(
                        (month) => (
                          <MenuItem key={month} value={month}>
                            {month} {month === 1 ? "Month" : "Months"}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  </FormControl>
                </InputGroup>
              </CombineInputGroup>

              <CombineInputGroup>
                <InputGroup>
                  <Label htmlFor="tenantPreference">
                    Preferred Tenant Type
                  </Label>
                  <FormControl fullWidth>
                    <Select
                      className="px-2"
                      value={values.tenantPreference}
                      onChange={(e) =>
                        setFieldValue("tenantPreference", e.target.value)
                      }
                      displayEmpty
                      sx={{ height: "46px" }}
                    >
                      <MenuItem value="" disabled>
                        <span className="text-gray-400">
                          Select tenant type
                        </span>
                      </MenuItem>
                      {preferredTenantOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {touched.tenantPreference && errors.tenantPreference && (
                    <p className="text-rose-600 text-sm mt-1">
                      {errors.tenantPreference}
                    </p>
                  )}
                </InputGroup>
                <InputGroup>
                  <Label htmlFor="building">Building/Tower/Block</Label>
                  <FieldWrapper>
                    <Field
                      type="text"
                      name="building"
                      id="building"
                      autoComplete="off"
                      // required
                      className={getInputClass(false)}
                    />
                  </FieldWrapper>
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="flatNo">Flat/House No</Label>
                  <FieldWrapper>
                    <Field
                      type="text"
                      name="flatNo"
                      id="flatNo"
                      autoComplete="off"
                      // required
                      className={getInputClass(false)}
                    />
                  </FieldWrapper>
                </InputGroup>
              </CombineInputGroup>

              <AddressGrid>
                <AddressInput>
                  <InputGroup>
                    <Label htmlFor="address">Address *</Label>

                    <FieldWrappers className="w-full border border-none pl-0">
                      <Autocomplete
                        className="w-full"
                        onLoad={(autocomplete) =>
                          (autocompleteRef.current = autocomplete)
                        }
                        onPlaceChanged={() => handlePlaceSelect(setFieldValue)}
                        options={{
                          componentRestrictions: { country: "in" },
                          types: ["geocode"],
                        }}
                      >
                        <input
                          type="text"
                          value={values.address}
                          onChange={(e) => {
                            setFieldValue("address", e.target.value);
                          }}
                          placeholder="Enter address"
                          className={getInputClass(false)}
                          // className="w-full pr-6 bg-transparent border-none outline-none"
                        />
                      </Autocomplete>
                      {values.address && (
                        <button
                          type="button"
                          onClick={() => setFieldValue("address", "")}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                        >
                          <MdClose />
                        </button>
                      )}
                    </FieldWrappers>

                    {touched.address && errors.address && (
                      <p className="text-rose-600 text-sm mt-1">
                        {errors.address}
                      </p>
                    )}
                  </InputGroup>
                </AddressInput>
                <InputGroup>
                  <Label htmlFor="location">Location *</Label>
                  <FieldWrappers
                    onClick={() => setShowLocationModal(true)}
                    className="border border-gray-400"
                  >
                    <Field
                      type="text"
                      name="locationId"
                      id="locationId"
                      autoComplete="off"
                      readOnly
                      value={getLocationLabel()}
                      className="w-full border-none outline-none bg-transparent cursor-pointer"
                    />
                    <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                  </FieldWrappers>

                  {touched.locationId && errors.locationId && (
                    <p className="text-rose-600 text-sm mt-1">
                      {errors.locationId}
                    </p>
                  )}
                </InputGroup>
              </AddressGrid>
            </Wrapper>

            <BtnWrapper>
              <SubmitBtn type="submit" disabled={editPropBasicInfoLoading} className="text-sm  w-24  md:w-20 h-7 md:text-xs">
                {editPropBasicInfoLoading ? "Updating..." : "Update"}
              </SubmitBtn>
            </BtnWrapper>
            <SelectLocationModal
              isOpen={showLocationModal}
              onClose={() => setShowLocationModal(false)}
              options={filteredLocations}
              title="Select Location"
              selectedValue={values.locationId}
              onSelect={handleLocationSelect}
              searchPlaceholder="Search Location..."
              width="1/3"
            />
            <SelectOptionModal
              isOpen={isBhkModalOpen}
              onClose={() => setIsBhkModalOpen(false)}
              title="Select BHK"
              options={bhk_options}
              selectedValue={values.inventorySubType}
              onSelect={handleBhkSelect}
              searchPlaceholder="Search BHK..."
              width="1/3"
            />
          </Form>
        );
      }}
    </Formik>
  );
};

const FieldWrapper = tw.div`w-full`;
const BtnWrapper = tw.div`w-full flex items-center justify-end mt-4`;
// const SubmitBtn = tw.button`
// text-sm  w-24  md:w-20 h-7 grid place-items-center disabled:opacity-50 disabled:cursor-not-allowed  md:text-xs bg-primary-color hover:bg-hover-color text-white rounded-md shadow-md`;
const AddressGrid = tw.div`grid md:grid-cols-3 grid-cols-1 gap-4`;
const AddressInput = tw.div`grid md:col-span-2 col-span-1`;
export default PropertyBasicInfo;
